import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch } from '@hooks/index';
import useForm from '@hooks/useForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '@common-components/tooltip/Tooltip';
import { GIButton } from 'app-components';
import { Table } from 'react-bootstrap';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import UnsavedChangesDialog from '@components/common/modals/UnsavedChangesModal';
import { setTktTypeFormState } from '@actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';
import { DEFAULT_SELECT, DEFAULT_EXPIRATION_DATE, TICKET_TYPE_FORM_NAMES, FIELD_TYPES, ALERT_TYPES, CATEGORY_FORM_NAMES, DEFAULT_COL_SPAN, TKM_WORKFLOW_VERIFY, EXPIRATION_DATE_CONSTANT } from './ticketFormConfigs';
import { createObjectWrapper } from '@utils/miscellaneous/objectHelpers';

const StyledContainer = styled.div`
    max-height: 20vh;
    overflow-x: auto;
    overflow-y: scroll;
    border: 1px solid ${({ theme }) => theme.gray7};
`;

const TicketTypeForm = (props) => {
    const {
        initialFormValues,
        formFields,
        onSaveHandler,
        onCancelHandler,
        showUnsavedModal,
        showModalSetter,
        closeModalSetter,
    } = props;
    const [alertMsg, setAlertMsg] = useState('');
    const dispatch = useAppDispatch();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [ inputValidationErrors, setInputValidationErrors ] = useState({});
    const { values, isDirty, handlers, reset, setArrayValue } = useForm(initialFormValues);
    const [showSpanishTextField, setSpanishTextField] = useState(initialFormValues[CATEGORY_FORM_NAMES.SPANISH_NAME]);

    useEffect(() => {
        dispatch(setTktTypeFormState(isDirty));
    }, [isDirty]);
    
    const validateValues = () => {
        const errors = {};

        formFields.map(field => {
            if(field.name === CATEGORY_FORM_NAMES.SPANISH_NAME && !showSpanishTextField){
                return;
            }
            if (field.validation && field.validation.regExp) {
                const regExp = field.validation.regExp;
                values[field.name] = values[field.name].trim();

                if (regExp && !regExp.test(values[field.name]))  {
                    // Unique error msgs can be added if needed.
                    // For now there is one general msg on the top.
                    errors[field.name] = true;
                }
            }
            if (values[field.name] === DEFAULT_SELECT) {
                errors[field.name] = true;
            }
        });

        let isValid = Object.keys(errors).length <= 0;
        setInputValidationErrors({ ...errors });
        !isValid && setAlertMsg(ALERT_TYPES.VALIDATION);
        return isValid;
    };
    
    const handleClick = (e, index, requiredField) => {
        const fieldValue = e.target.checked;
        const scrollTop = scrollRef.current ? scrollRef.current.scrollTop : 0;
        setArrayValue(FIELD_TYPES.CUSTOM_FIELD, index, fieldValue, requiredField );

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollTop;
            }
        },0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAlertMsg('');
        if (validateValues()) {
            let errorMsg = await onSaveHandler(values);
            if (errorMsg) {
                setAlertMsg(ALERT_TYPES[errorMsg]);
            } else {
                reset();
            }
        }
    };

    const handleFormCancelAction = () => {
        if(isDirty) {
            showModalSetter();
            return;
        }
        handleDrawerFormCancel();
    };

    const handleDrawerFormCancel = () => {
        setAlertMsg('');
        reset();
        onCancelHandler();
    };

    const onToggleSpanishTextField = e => {
        e.preventDefault();
        setSpanishTextField(!showSpanishTextField);
        values[CATEGORY_FORM_NAMES.SPANISH_NAME] = '';
    };

    const getSpanishTextField = (fieldName, field, error) => {
        if (values.permission !== 'TKM_WORKFLOW_PUBLIC1') {
            let spanishTextPresent = values[CATEGORY_FORM_NAMES.SPANISH_NAME];
            setSpanishTextField(spanishTextPresent);
            return;
        }
        if (!showSpanishTextField) {
            return (
                <a href="" onClick={onToggleSpanishTextField} data-testid="add-spanish-name" aria-label="Add spanish name field">
                    + <FormattedMessage id="ticket_management.form_header.spanish_name" />
                </a>
            );
        }
        return (
            <label key={fieldName} className="ticket-form__label">
                {field.label}
                <div className="ticket-form__icon-section">
                    <input
                        type="text"
                        className={`${error ? 'ticket-form__input ticket-form__input--error' : 'ticket-form__input'}`}
                        {...handlers[fieldName]}
                        maxLength={field.validation && field.validation.maxLength}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={error ? `${fieldName}-error` : undefined}
                        data-testid={`input-${fieldName}`}
                    />
                    <i className="icon" onClick={onToggleSpanishTextField} role='button' aria-label="Remove Spanish name field" data-testid="remove-spanish-name">
                        <FontAwesomeIcon icon="trash-alt" size="1x" />
                    </i>
                </div>
            </label>
        );
    };
   
    const constructFormFields = (formFields) => {
        const index = formFields.findIndex(field => field.name === TICKET_TYPE_FORM_NAMES.DUE_DATE);
        const EXPIRATION_DATE_CONSTANT_USE = EXPIRATION_DATE_CONSTANT;
        if (values.permission === TKM_WORKFLOW_VERIFY) {
            if (!formFields[index].options.includes(DEFAULT_EXPIRATION_DATE)) {
                formFields[index].options.push(DEFAULT_EXPIRATION_DATE);
            }
        } else {
            if (values.expDate && values.expDate.toString() === EXPIRATION_DATE_CONSTANT_USE.toString()) {
                values.expDate = DEFAULT_SELECT;
            }
            if (formFields[index] && formFields[index].options.includes(DEFAULT_EXPIRATION_DATE)) {
                formFields[index].options.pop();
            }
        }
        // eslint-disable-next-line no-undef
        const rows: JSX.Element[] = [];
        // eslint-disable-next-line no-undef
        let currentRow: JSX.Element[] = [];
        let currentRowSpan = 0;
        formFields.forEach((field, index) => {
            const fieldName = field.name;
            let error = inputValidationErrors && inputValidationErrors[fieldName];
            const colSpan = field.colSpan || DEFAULT_COL_SPAN;
            const fieldElement = (() => {
                switch (field.type) {
                    case FIELD_TYPES.TEXT_INPUT:
                        return (
                            <div key={fieldName} className={`col-${colSpan}`}>
                                <label className="ticket-form__label required-true">
                                    {field.label}
                                </label>
                                <Tooltip id="" index={`form-field-${fieldName}`} triggerClassName='margin-lr-10' content={`ticket_management.ticketTypes.form.tooltip.${fieldName}`} icon='info-circle' />
                                <input type={field.type}
                                    className={`${error ? 'ticket-form__input ticket-form__input--error' : 'ticket-form__input'}`}
                                    {...handlers[fieldName]}
                                    maxLength={field.validation?.maxLength}
                                    aria-invalid={error ? 'true' : 'false'}
                                    aria-describedby={error ? `${fieldName}-error` : undefined}
                                    data-testid={`input-${fieldName}`}
                                />
                            </div>
                        );
                    case FIELD_TYPES.SPANISH_NAME:
                        return (
                            <div id='spanishTxtField' key={fieldName} className={`col-${colSpan}`} data-testid="spanish-text-field-container">
                                {getSpanishTextField(fieldName, field, error)}
                            </div>
                        );
                    case FIELD_TYPES.CHECKBOX_INPUT:
                        return (
                            <div key={fieldName} className={`col-${colSpan}`} data-testid={`checkbox-container-${fieldName}`}>
                                <div className='custom-checkbox'>
                                    <input id={fieldName} className='ticket-form__checkbox' checked={values[fieldName]}
                                        type={FIELD_TYPES.CHECKBOX_INPUT} {...handlers[fieldName]} aria-checked={values[fieldName]} 
                                        aria-labelledby={`${fieldName}-label`} data-testid={`checkbox-input-${fieldName}`} />
                                    <label id={`${fieldName}-label`} htmlFor={fieldName} className='ticket-form__label ticket-form__label--checkbox' data-testid={`checkbox-label-${fieldName}`}>
                                        <span className='checkbox-icon' aria-hidden="true"><i className='fas fa-check'></i></span>
                                        {field.label}
                                        {fieldName === TICKET_TYPE_FORM_NAMES.ALLOW_UPLOAD && 
                                            (<Tooltip id="" triggerClassName='margin-lr-10' index={`form-field-${fieldName}`} content={`ticket_management.ticketTypes.form.tooltip.${fieldName}`} 
                                                icon='info-circle' aria-label={`Tooltip for ${field.label}`} data-testid={`tooltip-${fieldName}`}/>)
                                        }
                                    </label>
                                </div>
                            </div>
                        );
                    case FIELD_TYPES.SELECT:
                        return (
                            <div key={fieldName} className={`col-${colSpan}`}>
                                <label className="ticket-form__label">
                                    {field.label}
                                    <span className="required-field required-true"></span>
                                    <Tooltip id="" index={`form-field-${fieldName}`} content={`ticket_management.ticketTypes.form.tooltip.${fieldName}`}  icon='info-circle' />
                                    <div className='custom-select-wrapper'>
                                        <select
                                            key={fieldName}
                                            className={`${error ? 'ticket-form__select ticket-form__input--error' : 'ticket-form__select'}`}
                                            {...handlers[fieldName]}
                                            aria-invalid={error ? 'true' : 'false'}
                                            aria-describedby={error ? `${fieldName}-error` : undefined}
                                            data-testid={`select-${fieldName}`}
                                        >
                                            {field.options.map((option, index) => {
                                                const { label = option } = option;
                                                let { value = option } = option;
                                                const isDefaultExpirationDate = option === DEFAULT_EXPIRATION_DATE && fieldName === TICKET_TYPE_FORM_NAMES.DUE_DATE;
                                                if (isDefaultExpirationDate) {
                                                    value = EXPIRATION_DATE_CONSTANT_USE;
                                                }
                                                return (
                                                    <option key={index} value={value}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <span className="custom-arrow">
                                            <FontAwesomeIcon icon="chevron-down" />
                                        </span>
                                    </div>
                                </label>
                            </div>
                        );
                    case FIELD_TYPES.CUSTOM_FIELD: {
                        const customFieldLen = initialFormValues[fieldName].length;
                        if (!customFieldLen) return null;
                        return (   
                            <div key={fieldName} className="col-12" data-testid={`custom-field-container-${fieldName}`}>
                                <fieldset aria-labelledby={`custom-fieldset-label-${fieldName}`} data-testid={`custom-fieldset-${fieldName}`} >
                                    <div id={`custom-fieldset-label-${fieldName}`} className='lbl-fieldset container-flex' data-testid={`custom-fieldset-label-${fieldName}`}>
                                        <h4><FormattedMessage id="ticket_management.form_header.custom_field" /></h4>
                                        <Tooltip id="" index={`form-field-${fieldName}`} content={`ticket_management.ticketTypes.form.tooltip.${fieldName}`} icon='info-circle' aria-label={`Tooltip for custom field ${fieldName}`} data-testid={`tooltip-${fieldName}`} />
                                    </div>
                                    <StyledContainer ref={scrollRef} className='custom-field-tbl-holder' role='region' aria-labelledby={`custom-fieldset-label-${fieldName}`} data-testid={`custom-field-scroll-container-${fieldName}`}>
                                        <Table className="table tbl-custom-field" aria-label="Custom fields table" data-testid={`custom-field-table-${fieldName}`}>
                                            <thead>
                                                <tr>
                                                    <th><FormattedMessage id="ticket_management.custom_field.form_header.name" /></th>
                                                    <th><FormattedMessage id="ticket_management.custom_field.form_header.type" /></th>
                                                    <th><FormattedMessage id="ticket_management.form_header.custom_field.required" /></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {createObjectWrapper(initialFormValues[fieldName])
                                                    .map((item: { id: string; name: string; type: string }, index) => {
                                                        return (
                                                            <CustomFieldRow
                                                                key={item.id}
                                                                row={item}
                                                                values={values}
                                                                index={index}
                                                                field={field}
                                                                data-testid={`custom-field-row-${index}`}
                                                            />
                                                        );
                                                    })
                                                    .valueOf()}
                                            </tbody>
                                        </Table>
                                    </StyledContainer>
                                </fieldset>
                            </div>
                        );    
                    }   
                    case FIELD_TYPES.LEGEND:
                        return (
                            <div id='legendLbl' key={fieldName} className={`col-${colSpan}`} data-testid={`custom-field-row-${index}`}>
                                <label id={`legend-label-${fieldName}`} htmlFor={`legend-${fieldName}`} data-testid={`legend-label-${fieldName}`} className="ticket-form__label padding-top-1-rem">
                                    {field.label}
                                </label>
                            </div>
                        );
                    default:
                        return null;
                }
            })();
            const fullColSpan = DEFAULT_COL_SPAN * 2;
            if (colSpan === fullColSpan) {
                if (currentRow.length > 0) {
                    rows.push(<div className="row" role="row" key={`row-${rows.length}`} data-testid={`row-${rows.length}`}>{currentRow}</div>);
                    currentRow = []; 
                    currentRowSpan = 0; 
                }
                const rowClassName = React.isValidElement(fieldElement) ? 'row border-bottom-gray' : 'row';
                rows.push(<div className={rowClassName} key={`row-${rows.length}`} role='row' data-testid={`row-${rows.length}`}>{fieldElement}</div>);
            } else {
                if (currentRowSpan + colSpan > 12) {
                    rows.push(<div className="row" key={`row-${rows.length}`} role="row" data-testid={`row-${rows.length}`}>{currentRow}</div>);
                    currentRow = [];
                    currentRowSpan = 0;
                }

                if (fieldElement) {
                    currentRow.push(fieldElement);
                }
                currentRowSpan += colSpan;
                if (index === formFields.length - 1) {
                    rows.push(<div className="row" key={`row-${rows.length}`} role='row' data-testid={`row-${rows.length}`}>{currentRow}</div>);
                }
            }
        });
        return rows;
    };
    const CustomFieldRow = ({ row , values, index, field }) => {
        const { name, type, id } = row;
        return (
            <tr key={id} data-testid={`custom-field-row-${index}`} role="row">
                <td role="cell" data-testid={`custom-field-name-${index}`}>
                    <div className='custom-checkbox'>
                        <input id={id} className="ticket-form__checkbox" checked={values[field.type][index].selected} type={FIELD_TYPES.CHECKBOX_INPUT} 
                            onClick={(e) => handleClick(e, index, false)}  aria-checked={values[field.type][index].selected} aria-labelledby={`label-${id}`} data-testid={`custom-field-checkbox-${index}`}/>
                        <label htmlFor={id} className='ticket-form__label ticket-form__label--checkbox' title={name} data-testid={`custom-field-label-${index}`}>
                            <span className='checkbox-icon' aria-hidden="true"><i className='fas fa-check'></i></span>
                            <span className='table-cell'>{name}</span>
                        </label>
                    </div>
                </td>
                <td role="cell" data-testid={`custom-field-type-${index}`}>{type}</td>
                <td role="cell" className="text-right" data-testid={`custom-field-required-${index}`}>
                    <label className="switch">
                        <input
                            className="ticket-form__checkbox"
                            disabled={!values[field.type][index].selected}
                            checked={values[field.type][index].required}
                            onClick={(e) => handleClick(e, index, true)}
                            type={FIELD_TYPES.CHECKBOX_INPUT}
                            id={id}
                            aria-checked={values[field.type][index].required}
                            aria-labelledby={`required-label-${id}`}
                            data-testid={`custom-field-required-checkbox-${index}`}
                        />
                        <span className="slider round" aria-hidden="true" ></span>
                    </label>
                </td>
            </tr>
        );
    };

    const memoizedConstructedFormFields = useMemo(() => constructFormFields(formFields), [values, values[FIELD_TYPES.CUSTOM_FIELD] , formFields, inputValidationErrors, showSpanishTextField]);
    const editedTicketTypeLabel = initialFormValues?.type || '';
    return (
        <form
            className="ticket-type-form"
            id="ticketTypeForm"
            onSubmit={handleSubmit}
            aria-labelledby="tktTypeFormHeader"
            data-testid="ticket-type-form"
        >
            <div id='tktTypeFormHeader' className='d-flex justify-content-between align-item-center' data-testid="tkt-type-form-header">
                <div>
                    <h4 title={editedTicketTypeLabel} data-testid="ticket-type-form-title">{editedTicketTypeLabel}</h4>
                </div>
                <div id='tktTypeActionHandler' className='d-flex justify-content-end' data-testid="tkt-type-action-handler">        
                    <GIButton id='btnCancel' type='button' labelId='button.cancel' variant='secondary' onClickHandler={handleFormCancelAction} aria-label="Cancel form" data-testid="btn-cancel" />
                    <GIButton id='btnSubmit' labelId='button.save' variant='primary' onClickHandler={handleSubmit} aria-label="Save form" data-testid="btn-submit"/>
                </div>
            </div>
            <div className='grid-container tkt-type-form-body' role="region" aria-labelledby="tktTypeFormHeader" data-testid="tkt-type-form-body">
                <ConditionalRendering isTrue={alertMsg.length > 0}>
                    <AlertMessage showIcon={false} key={alertMsg} content={<FormattedMessage id={alertMsg} />} type="error" aria-live="assertive" data-testid="alert-message" />
                </ConditionalRendering>
                {memoizedConstructedFormFields}
            </div>
            <ConditionalRendering isTrue={showUnsavedModal}>
                <UnsavedChangesDialog 
                    headerId='common.unSavedChanges.modal.header.text' 
                    message={<FormattedMessage id='common.unSavedChanges.modal.body.text' />} 
                    onLeave={handleDrawerFormCancel} 
                    onStay={closeModalSetter} 
                    aria-labelledby="unsaved-changes-dialog"
                    data-testid="unsaved-changes-dialog"
                />
            </ConditionalRendering>
        </form>
    );
};

export default TicketTypeForm;